import { cva } from 'cva';
import {
  Autocomplete,
  type AutocompleteProps,
  useFilter,
} from 'react-aria-components/Autocomplete';

import { Popover } from '../popover/popover';
import { SearchField } from '../search_field/search_field';
import type { DropdownListProps } from './dropdown_list';
import { DropdownListContent } from './dropdown_list_content';

import styles from './searchable_dropdown_list.module.css';

const popoverVariants = cva({
  base: styles.popover,
  variants: {
    matchTriggerWidth: {
      true: styles.popover_match_trigger_width,
      false: styles.popover_default_width,
    },
  },
});

export type SearchableDropdownListProps = DropdownListProps & {
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  searchFieldLabel?: string;
  filter?: AutocompleteProps['filter'];
};

export function SearchableDropdownList({
  options,
  selectionMode,
  size = 'm',
  searchValue,
  onSearchValueChange,
  optionsListLabel = 'Options',
  searchFieldLabel = 'Search options',
  noResultsText = 'No results found',
  loadingText = 'Loading options…',
  matchTriggerWidth = false,
  className,
  renderOption,
  isLoading,
  onLoadMore,
  placement = 'bottom end',
  maxHeight = 210,
  filter,
  ...popoverProps
}: SearchableDropdownListProps) {
  const { contains } = useFilter({ sensitivity: 'base' });

  return (
    <Popover
      {...popoverProps}
      className={popoverVariants({ matchTriggerWidth, className })}
      placement={placement}
    >
      <Autocomplete
        filter={typeof filter !== 'undefined' ? filter : contains}
        inputValue={searchValue}
        onInputChange={onSearchValueChange}
      >
        <SearchField
          autoFocus
          className={styles.search_field}
          aria-label={searchFieldLabel}
        />
        <DropdownListContent
          options={options}
          selectionMode={selectionMode}
          size={size}
          optionsListLabel={optionsListLabel}
          noResultsText={noResultsText}
          loadingText={loadingText}
          renderOption={renderOption}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
          maxHeight={maxHeight}
        />
      </Autocomplete>
    </Popover>
  );
}
